import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import CatalogBrandHeader from '/@/components/catalog/catalog-brand-header';
import CatalogSidebar from '/@/components/catalog-sidebar';
import CatalogWarning from '/@/components/catalog-warning';
import DKProtect from '/@/components/dk-protect';
import Drawing from '/@/components/drawing';
import TechnologySlider from '/@/components/technology-slider';
import settings from '/@/core/settings';
import {
    useAllCatalogProducts,
    useCornTechnologies,
    useRTVCatalogProducts,
    useSoyTechnologies,
} from '/@/hooks/catalog';
import { _ } from '/@/locale';
import { checkInternal } from '/@/settings/menu.js';
import { setCatalogZone } from '/@/store/catalog/actions';
import { useCatalogConfig, useCatalogZones } from '/@/store/catalog/apiProviders';
import {
    getCatalogBrandBySlug,
    getCatalogCropType,
    getCatalogProductType,
    getCatalogSortField,
    getCatalogZone,
    getCatalogZonesByBrandWithCountry,
    getCropProtectionTargetSearch,
} from '/@/store/catalog/selectors';
import { getAppInstance, getSessionCountry, getUser } from '/@/store/session/selectors';
import { isMobile } from '/@/utils';
import useWindowSize from '/@/utils/hooks/window-size';
import { reverse } from '/@/utils/router';
import Acceleron from '/@/v3/components/acceleron';
import NewTechnologyList from '../new-technology-list';
import CatalogProduct from '../product-selection/product';
import styles from './styles.module.css';

const NewProductSelection = (props) => {
    const history = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector(getUser);
    const appInstance = useSelector(getAppInstance);
    const country = useSelector(getSessionCountry);
    const brand = useSelector((state) => getCatalogBrandBySlug(state, props.slug));
    const { data: config } = useCatalogConfig({ brand: brand.id });
    const cropType = useSelector(getCatalogCropType);
    const productType = useSelector(getCatalogProductType);
    const target = useSelector(getCropProtectionTargetSearch);
    const sortField = useSelector(getCatalogSortField);
    const [options, setOptions] = useState<{ crops: string[]; products: string[] }>();

    const catalogZonesQuery = useCatalogZones({ page: 1, pageSize: 10000, crop: props.type, type: 'catalog' });
    const catalogZones = useMemo(() => catalogZonesQuery.data?.results || [], [catalogZonesQuery.data?.results]);

    const zones = useMemo(() => {
        if (props.type === 'crop_protection') return [];
        return getCatalogZonesByBrandWithCountry(country, brand, 'catalog', catalogZones);
    }, [catalogZones, country, brand, props.type]);

    const zone = useSelector((state) => {
        if (props.type === 'crop_protection') return {};
        return getCatalogZone(state, catalogZones);
    });

    const type = props.type === 'refuge' ? 'corn' : props.type;
    const technologiesCornQuery = useCornTechnologies({ page: 1, pageSize: 10000 });
    const technologiesSoyQuery = useSoyTechnologies({ page: 1, pageSize: 10000 });
    const technologies = (
        (props.type === 'corn' ? technologiesCornQuery.data?.results : technologiesSoyQuery.data?.results) || []
    ).filter((t) => t.displayInCatalog);

    const [showMore, setShowMore] = useState(false);
    const windowSize = useWindowSize();

    const { data, isValidating: isLoading } = useAllCatalogProducts(
        {
            country: country.code,
            brand: brand.name,
            crop: type,
            target,
            sortField,
            cropProtectionCrop: cropType,
            cropProtectionProductType: productType,
            zone: zone ? zone.id : null,
        },
        {
            enabled: ['corn', 'soy'].includes(type) && !zone ? false : true,
        }
    );
    const { data: rtvData, isValidating: isLoadingRTV } = useRTVCatalogProducts({
        user,
        country: country.code,
        target,
        sortField,
        cropProtectionCrop: cropType,
        cropProtectionProductType: productType,
        appInstance,
        crop: type,
    });
    const items = data?.results || [];
    const rtvItems = rtvData ? rtvData?.results : [];
    const uniqueCrops = Array.from(
        new Map(
            items
                ?.flatMap((product) => product.crops || [])
                .map((crop) => [JSON.stringify(crop), { id: crop.id, ...crop.name }])
        ).values()
    );
    const uniqueProducts = Array.from(
        new Map(items?.map((product) => product.type).map((crop) => [JSON.stringify(crop), crop])).values()
    );

    useEffect(() => {
        if (data && !options) {
            setOptions({ crops: uniqueCrops, products: uniqueProducts });
        }
    }, [data, options]);

    const staticOptions = options || {};

    const enableSideBar = zones.length >= 2 || brand.type === 'crop_protection';
    const sliderData = technologies.map((tec) => {
        return {
            label: tec.id,
            name: tec.name,
            logoPath: tec.logoImage,
            imgPath: tec.images.find((i) => i.brand === brand.id)?.image,
        };
    });

    useEffect(() => {
        if (zones.length > 0 && !zone) {
            dispatch(setCatalogZone(zones?.[0]?.id));
        }
    }, [window.location.href, zones]);

    const handleViewCompleteCatalog = () => {
        const { type } = props;
        history(
            reverse('catalog.Biotechnology', {
                params: { type },
            })
        );
    };

    useEffect(() => {
        if (!appInstance.enableInternalProfileCatalog && props.internal === 'bayer') {
            const type = props.type;
            const slug = props.slug;
            history(
                reverse('catalog.ProductSelection', {
                    params: { type, slug },
                })
            );
        }
    }, [props.internal]);

    const handleItemSelection = (itemId) => {
        history(
            reverse('catalog.ProductDetail', {
                params: {
                    type: props.type,
                    slug: props.slug,
                    itemId,
                    zoneId: zone && zone.id ? zone.id : 1, //avoids undefined zoneId in product detail url
                    internal: props.internal,
                },
            })
        );
    };

    const products =
        props.internal === 'bayer' && checkInternal(user, appInstance) && props.type === 'crop_protection'
            ? rtvItems
            : items;

    const filteredItems = !isMobile() ? products : showMore ? products : products.slice(0, 3);

    return (
        <Fragment>
            <div className={styles.container}>
                {enableSideBar && (
                    <div className={styles.sidebar}>
                        <CatalogSidebar
                            options={staticOptions}
                            internal={checkInternal(user, appInstance) && props.internal}
                            brand={brand}
                        />
                    </div>
                )}

                <div className={styles.content}>
                    {isMobile() ? (
                        <CatalogSidebar
                            options={staticOptions}
                            internal={checkInternal(user, appInstance) && props.internal}
                            brand={brand}
                        >
                            <CatalogBrandHeader config={config} brand={brand} />
                        </CatalogSidebar>
                    ) : (
                        <CatalogBrandHeader config={config} brand={brand} />
                    )}

                    <div>
                        {((!!products && !!products.length) || isLoading) && (
                            <div className={styles.gridContainer}>
                                <div className={classNames(styles.grid, country.code === 'MX' && styles.mxGrid)}>
                                    {items.length ? (
                                        filteredItems.map((item) => {
                                            return (
                                                <CatalogProduct
                                                    cropProtection={props.type === 'crop_protection'}
                                                    key={item.id}
                                                    profile={item}
                                                    brand={brand}
                                                    type={props.type}
                                                    handleClick={handleItemSelection}
                                                />
                                            );
                                        })
                                    ) : (
                                        <Fragment>
                                            {Array.from(new Array(8)).map((item, index) => (
                                                <Skeleton key={index} variant='rectangular' width='100%' height={300} />
                                            ))}
                                        </Fragment>
                                    )}
                                    {isMobile() && products.length >= 4 && (
                                        <div className={styles.btnContainer}>
                                            <div
                                                onClick={() => setShowMore(!showMore)}
                                                className={classNames(
                                                    styles.btn,
                                                    styles[`theme-${settings.app.theme}`]
                                                )}
                                            >
                                                {showMore ? _('Mostrar menos') : _('Mostrar más')}
                                            </div>
                                        </div>
                                    )}
                                    {isMobile() && <CatalogWarning brand={brand} />}
                                </div>
                                {!isMobile() && <CatalogWarning isDetailView={false} brand={brand} />}
                            </div>
                        )}
                        {(!products || !products.length) && (!isLoading || !isLoadingRTV) && (
                            <div className={styles.empty}>
                                <Drawing
                                    type='library-not-found'
                                    title={_('No hay productos disponibles para los filtros seleccionados.')}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {config && config.enableTechnologyDeklabProtect && <DKProtect />}
            {technologies.length > 0 && config && config.enableTechnologyCatalog && (
                <Fragment>
                    <div className={styles.technologies}>
                        <div className={styles.watchMore}>
                            <Typography sx={{ color: (t) => t.palette.primary.main, m: 2 }}>
                                {_('Tecnologías Disponibles')}
                            </Typography>

                            {windowSize.width < 1200 && (
                                <div className={styles.slider}>
                                    <TechnologySlider data={sliderData} onClick={handleViewCompleteCatalog} />
                                </div>
                            )}
                            <Button variant='contained' onClick={handleViewCompleteCatalog}>
                                {_('Ver Catálogo')}
                            </Button>
                        </div>
                    </div>

                    {windowSize.width >= 1200 && (
                        <NewTechnologyList type={props.type} brand={brand} onClick={handleViewCompleteCatalog} />
                    )}
                </Fragment>
            )}
            {<Acceleron brand={brand.type} />}
        </Fragment>
    );
};

export default NewProductSelection;
